import clsx from 'clsx';
import ErrorText from 'core/modules/atoms/ErrorText';
import { Button, DatePicker, Icon, InputCheckbox, SelectCustomizable, utilsSelectCustomizable } from 'ui';
import InputTimeRange from 'core/modules/atoms/InputTimeRange';
import * as coreEntities from 'core/store/index';
import { weekdays } from 'core/variables/constant';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

// require('moment/locale/es.js');

// Un `timetable = []` en la firma crearía un array nuevo en cada render y el useEffect de [timetable],
// que hace setDays, entraría en bucle.
const EMPTY_TIMETABLE = [];

function noop() {}

const PeriodForm = ({
  title,
  t,
  id,
  index,
  onChange = noop,
  onChangeDays = noop,
  periodName = '',
  timetable = EMPTY_TIMETABLE,
  applySameShechule = noop,
  appliedSameShechule = false,
  enableAdvancedProgramming = false,
}) => {
  const lang = useSelector((state) => coreEntities.i18n.selectors.getLanguage(state));
  const addDayButtonRef = useRef(null);
  const [errorStartDate, setErrorStartDate] = useState(false);
  const [errorEndDate, setErrorEndDate] = useState(false);
  const [minDateFrom, setMinDateFrom] = useState(null);
  const [minDateTo, setMinDateTo] = useState(null);

  const INIT_DAY = {
    guid: new Date().getTime(),
    value: ['08:00', '09:00'],
    day: 'monday',
  };

  const [days, setDays] = useState(timetable ? getCurrentTimeTable().days : [INIT_DAY]);
  let PARSE_WEEKDAYS = utilsSelectCustomizable.getOptionsProperties(weekdays, 'name', 'id');
  PARSE_WEEKDAYS = PARSE_WEEKDAYS.map((item) => {
    const labelKey = typeof item.label === 'string' ? item.label : '';
    return { ...item, label: t(`weekdays:${labelKey}`) };
  });

  useEffect(() => {
    setDays(timetable.find((item) => item.id === id) ? timetable.find((item) => item.id === id).days : []);
    if (timetable[index - 1]?.end_at || timetable[index - 1]?.end_date) {
      const originalDate = new Date(timetable[index - 1]?.end_at ? timetable[index - 1]?.end_at : timetable[index - 1]?.end_date);
      const newDate = new Date(originalDate);
      newDate.setDate(originalDate.getDate() + 1);
      setMinDateFrom(newDate);
    }

    if (getCurrentTimeTable()?.start_at || getCurrentTimeTable()?.start_date) {
      const originalDate = new Date(!enableAdvancedProgramming ? getCurrentTimeTable()?.start_at : getCurrentTimeTable()?.start_date);
      const newDate = new Date(originalDate);
      newDate.setDate(originalDate.getDate() + 1);
      setMinDateTo(newDate);
    }
  }, [timetable]);

  function getIndexOfPeriod() {
    return timetable.findIndex((item) => item.id === id);
  }

  function onNewDay() {
    setDays([...days, INIT_DAY]);
    onChangeDays([...days, INIT_DAY], getIndexOfPeriod());
  }

  function onDeleteDay(dayId) {
    setDays(days.filter((day) => day.guid !== dayId));
    onChangeDays(
      days.filter((day) => day.guid !== dayId),
      getIndexOfPeriod()
    );
    // La papelera enfocada se desmonta con su fila (`key={day.guid}`): sin esto el foco caería al body.
    addDayButtonRef.current?.focus();
  }

  function withContext(label, context) {
    return context ? t('new-course:Label with context', { label, context }) : label;
  }

  function getCurrentTimeTable() {
    return timetable.find((item) => item.id === id) ? timetable.find((item) => item.id === id) : [INIT_DAY];
  }

  function onChangeDay(e, dayId, attr) {
    let newDays = null;
    if (attr === 'range') {
      if (e.target.validity && e.target.validity.valid === false) {
        newDays = days.map((day) => (day.guid === dayId ? { ...day, error: true } : day));
        setDays(newDays);
      } else {
        newDays = days.map((day) => (day.guid === dayId ? { ...day, error: false, value: e.target.value } : day));
        setDays(newDays);
      }
    } else {
      newDays = days.map((day) => (day.guid === dayId ? { ...day, [attr]: e.value } : day));
      setDays(newDays);
    }
    onChangeDays(newDays, getIndexOfPeriod());
  }

  function onChangeDate(e, attr) {
    if (!e) {
      if (attr === 'start_at' || attr === 'start_date') {
        setErrorStartDate(true);
      } else {
        setErrorEndDate(true);
      }
    } else {
      if (attr === 'start_at' || attr === 'start_date') {
        setErrorStartDate(false);
      } else {
        setErrorEndDate(false);
      }
      onChange(e, attr, getIndexOfPeriod(), true);
    }
  }

  function renderDay(day, i) {
    const weekday = PARSE_WEEKDAYS.find((option) => option.value === day.day);
    const dayContext = weekday ? withContext(weekday.label, periodName) : periodName;
    const row = i + 1;

    function handleDeleteDay() {
      onDeleteDay(day.guid);
    }

    return (
      <div className="period-form__day" key={day.guid}>
        <div className="period-form__day-week">
          <SelectCustomizable
            label={
              <>
                {/* El texto visible no cambia (el espaciador de las filas 2+ alinea la papelera); el nombre va en sr-only. */}
                <span aria-hidden="true">{i === 0 ? t('new-course:Day') : <>&nbsp;</>}</span>
                <span className="sr-only">{withContext(t('new-course:Day of row', { row }), periodName)}</span>
              </>
            }
            options={PARSE_WEEKDAYS}
            placeholder={t('placeholder:Select')}
            onChange={(e) => onChangeDay(e, day.guid, 'day')}
            value={weekday}
            isSearchable={false}
          />
        </div>
        <div className="period-form__day-schedule">
          <InputTimeRange
            endTimeLabel={withContext(t('new-course:End time of row', { row }), dayContext)}
            error={day.error}
            label={<span>&nbsp;</span>}
            onChange={(e) => onChangeDay(e, day.guid, 'range')}
            startTimeLabel={withContext(t('new-course:Start time of row', { row }), dayContext)}
            value={day.value}
          />
        </div>
        <button
          type="button"
          className="period-form__day-trash"
          aria-label={withContext(t('new-course:Delete row', { row }), dayContext)}
          onClick={handleDeleteDay}
        >
          <Icon name="trash" />
        </button>
      </div>
    );
  }

  function renderErrorDate() {
    return (
      <div className="period-form__date-error">
        <ErrorText text={t('new-course:You must enter a valid date')} />
      </div>
    );
  }

  const fromLabel = withContext(t('common:From'), periodName);
  const toLabel = withContext(t('common:To'), periodName);

  return (
    <fieldset className="period-form">
      {/* Sin title, el nombre ya se ve en la cabecera del acordeón: la leyenda solo hace falta para el lector. */}
      {(title || periodName) && <legend className={title ? 'period-form__title' : 'sr-only'}>{title || periodName}</legend>}
      <div className="period-form__form">
        <div className={clsx('period-form__dates', enableAdvancedProgramming && 'period-form__dates--advanced')}>
          <span className="period-form__subtitle">{t('Dates')}</span>
          <div className="period-form__range-date">
            <DatePicker
              ariaLabel={fromLabel}
              calendarButtonAriaLabel={withContext(t('buttons:Open calendar'), fromLabel)}
              label={t('common:From')}
              onChange={(newStart) => {
                onChangeDate(newStart, !enableAdvancedProgramming ? 'start_at' : 'start_date');
              }}
              value={!enableAdvancedProgramming ? getCurrentTimeTable().start_at : getCurrentTimeTable().start_date}
              shouldDisablePast={false}
              placeholder={!lang.includes('en') || !lang ? 'DD/MM/YYYY' : 'MM/DD/YYYY'}
              minDate={minDateFrom}
              hasYearPicker
            />
            {errorStartDate && renderErrorDate()}
            <div className="period-form__separator-container">
              <div className="period-form__separator"></div>
            </div>
            <DatePicker
              ariaLabel={toLabel}
              calendarButtonAriaLabel={withContext(t('buttons:Open calendar'), toLabel)}
              label={t('common:To')}
              onChange={(newFrom) => {
                onChangeDate(newFrom, !enableAdvancedProgramming ? 'end_at' : 'end_date');
              }}
              value={!enableAdvancedProgramming ? getCurrentTimeTable().end_at : getCurrentTimeTable().end_date}
              shouldDisablePast={false}
              placeholder={!lang.includes('en') || !lang ? 'DD/MM/YYYY' : 'MM/DD/YYYY'}
              minDate={minDateTo}
              hasYearPicker
            />
            {errorEndDate && renderErrorDate()}
          </div>
          {getCurrentTimeTable().error && (
            <div className="period-form__day-schedule-error">
              <ErrorText text={t('The course end date must be after the start date')} />{' '}
            </div>
          )}
        </div>
        {!enableAdvancedProgramming && (
          <div className="period-form__schedules">
            <div className="period-form__schedules-header">
              <span className="period-form__subtitle">{t('Shedules')}</span>
              {index === 0 && (
                <div className="period-form__all-period-checkbox">
                  <InputCheckbox label={t('Apply in all periods')} onChange={applySameShechule} checked={appliedSameShechule} />
                </div>
              )}
            </div>

            {days && (
              <div className={`period-form__schedules-form ${days.length === 1 && 'period-form__schedules-form--hide-delete'}`}>
                {days.map((day, i) => renderDay(day, i))}
              </div>
            )}
            <div className="period-form__add-day">
              <Button
                className="period-form__add-day-button"
                variant="plain"
                isIconOnly
                leftIcon="add-circle"
                ariaLabel={withContext(t('new-course:Add day'), periodName)}
                onClick={onNewDay}
                ref={addDayButtonRef}
              />
            </div>
          </div>
        )}
      </div>
    </fieldset>
  );
};

PeriodForm.propTypes = {
  title: PropTypes.string,
  t: PropTypes.func,
  index: PropTypes.number,
  onChange: PropTypes.func,
  timetable: PropTypes.array,
  id: PropTypes.string,
  onChangeDays: PropTypes.func,
  periodName: PropTypes.string,
  applySameShechule: PropTypes.func,
  appliedSameShechule: PropTypes.bool,
  enableAdvancedProgramming: PropTypes.bool,
  course: PropTypes.object,
};

export default PeriodForm;
